/**
 * Compact DSS pipeline strip — signal only, no ETA / marketing copy.
 */
import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/reduxHooks';
import { WorkflowStage } from '../../store/slices/workflowSlice';

const STEPS: {
  stage: WorkflowStage;
  label: string;
  short: string;
  path: string;
}[] = [
  { stage: WorkflowStage.WATER_PROFILE, label: 'Water', short: '1', path: '/water-quality' },
  { stage: WorkflowStage.CROP_FORMULATION, label: 'Crop', short: '2', path: '/formulation' },
  { stage: WorkflowStage.SALT_MANAGEMENT, label: 'Salts', short: '3', path: '/salt-library' },
  { stage: WorkflowStage.STOCK_SOLUTIONS, label: 'Recipe', short: '4', path: '/stock-solutions' },
  /** Results uses UNIFIED_CALCULATOR stage flag when finalized */
  { stage: WorkflowStage.UNIFIED_CALCULATOR, label: 'Results', short: '5', path: '/results' },
];

interface DssPipelineStripProps {
  currentStage?: WorkflowStage;
}

const DssPipelineStrip: React.FC<DssPipelineStripProps> = ({ currentStage }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const { stageStatus, canAdvanceToStage } = useAppSelector((s) => s.workflow);

  const unlocked = (stage: WorkflowStage) => {
    if (stage === WorkflowStage.WATER_PROFILE) return true;
    // Results reachable if recipe (stock) completed
    if (stage === WorkflowStage.UNIFIED_CALCULATOR) {
      return (
        Boolean(stageStatus[WorkflowStage.STOCK_SOLUTIONS]?.completed) ||
        Boolean(canAdvanceToStage[WorkflowStage.UNIFIED_CALCULATOR])
      );
    }
    return (
      Boolean(canAdvanceToStage[stage]) ||
      Boolean(stageStatus[stage]?.completed)
    );
  };

  return (
    <Box
      component="nav"
      aria-label="Recipe pipeline"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1 },
        flexWrap: 'wrap',
        py: 1.5,
        px: { xs: 1, sm: 2 },
        mb: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      {STEPS.map((step, index) => {
        const done = Boolean(stageStatus[step.stage]?.completed);
        // Treat Results step as done when calculator stage completed OR stock completed with results intent
        const isDone =
          step.stage === WorkflowStage.UNIFIED_CALCULATOR
            ? done || Boolean(stageStatus[WorkflowStage.STOCK_SOLUTIONS]?.completed)
            : done;
        const isCurrent = step.stage === currentStage;
        const canOpen = unlocked(step.stage) || isDone;

        return (
          <React.Fragment key={step.stage}>
            {index > 0 && (
              <Box
                aria-hidden
                sx={{
                  width: { xs: 8, sm: 16 },
                  height: 1,
                  bgcolor: isDone || isCurrent ? 'primary.main' : 'divider',
                  flexShrink: 0,
                }}
              />
            )}
            <Box
              component="button"
              type="button"
              disabled={!canOpen}
              onClick={() => canOpen && navigate(step.path)}
              aria-current={isCurrent ? 'step' : undefined}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                border: '1px solid',
                borderColor: isCurrent
                  ? 'primary.main'
                  : isDone
                    ? 'success.main'
                    : 'divider',
                bgcolor: isCurrent
                  ? 'primary.main'
                  : isDone
                    ? 'success.main'
                    : 'transparent',
                color: isCurrent || isDone ? 'common.white' : 'text.secondary',
                borderRadius: 1,
                px: 1.25,
                py: 0.5,
                cursor: canOpen ? 'pointer' : 'not-allowed',
                opacity: canOpen ? 1 : 0.45,
                font: 'inherit',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover:not(:disabled)': {
                  borderColor: 'primary.main',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor:
                    isCurrent || isDone
                      ? 'rgba(255,255,255,0.2)'
                      : 'action.hover',
                  color: isCurrent || isDone ? 'inherit' : 'text.secondary',
                }}
              >
                {isDone && !isCurrent ? '✓' : step.short}
              </Box>
              {!compact && (
                <Typography
                  component="span"
                  variant="body2"
                  fontWeight={isCurrent ? 700 : 500}
                  sx={{ color: 'inherit', lineHeight: 1 }}
                >
                  {step.label}
                </Typography>
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default DssPipelineStrip;
